"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowDownToLine, ArrowLeftRight, Check, Copy, Lock, Receipt, RefreshCcw, Wallet } from "lucide-react";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { sanitizeUrl } from "@/lib/helpers";
import { useUserBalance } from "@/features/wallet/hooks/useUserBalance";
import { lockedSplit } from "@/features/wallet/insights/figures";
// Loaded on first open, not with every page: the menu is in the header everywhere, the dialogs (and
// their flows) are opened rarely. They were already rendered only once chosen, so nothing else changes.
const DepositDialog = dynamic(() => import("@/features/wallet/v2/dialogs/DepositDialog"), {
  ssr: false
});
const SwapDialog = dynamic(() => import("@/features/wallet/v2/dialogs/SwapDialog"), {
  ssr: false
});
import type { Currency } from "@/features/currencies/types";
import { Popover, Spinner, toast } from "./ui";
import "./account.css";
import { withPercent } from "@/lib/percent";

/**
 * THE WALLET CONTROL.
 *
 * Signed out it renders NOTHING — not a disabled button, not a prompt. A bar
 * that offers a wallet to someone who has none is offering a dead end.
 *
 * THE DATA IS THE APP'S OWN, NOT A SECOND COPY. `useUserBalance` is the
 * existing hook: it lists the viewer's wallets, fans out one balance query per
 * wallet, enriches each with its native and token assets and sums the rial
 * value. React Query dedupes it against every other consumer, so mounting it
 * in the chrome costs one set of requests for the whole app rather than one
 * per screen — and it is `enabled`-gated on the token inside `useWallets`, so
 * a signed-out visitor fires nothing.
 *
 * WHAT CURRENCY IT SPEAKS. Rial to a Persian reader, tether to an English one
 * — the same rule the live-rates panel and the ticker follow. The hook only
 * sums rial, so the tether figure is that sum divided by the USDT rate from
 * `/currency`: the site's own published rate, the same number the ticker
 * shows. If that rate is missing the control says rial rather than inventing a
 * conversion.
 */

export default function WalletMenu({
  lang,
  currencies = []
}: {
  lang: "fa" | "en";
  /** From the locale layout's one `/currency` fetch — see `HomeV2Shell`. */
  currencies?: Currency[];
}) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => isFa ? fa : en;
  const href = (p: string) => `/${lang}${p}`;
  const token = useAuthStore(s => s.token);
  /** Which address was just copied, so the row can confirm it. */
  const [copied, setCopied] = useState<string | null>(null);
  /** Add funds and swap open their own dialog right here, over the page — no trip to /wallet. */
  const [dialog, setDialog] = useState<"deposit" | "swap" | null>(null);

  // Hooks may not be called conditionally, so this runs either way — it is
  // inert without a token because `useWallets` is `enabled: !!token`.
  const {
    wallets,
    totalIrr,
    isLoading,
    refetch
  } = useUserBalance();
  if (!token) return null;
  const irr = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US", {
    maximumFractionDigits: 0
  });
  const amount = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US", {
    maximumFractionDigits: 4
  });
  const pct = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US", {
    maximumFractionDigits: 0
  });
  const usdtRate = Number(currencies.find(c => String(c.symbol).toLowerCase() === "usdt")?.price_sell_irr ?? 0);
  const asTether = !isFa && usdtRate > 0;
  /** A rial value in the locale's currency, unit included — the same reading as the headline. */
  const money = (rial: number) => asTether ? {
    v: new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2
    }).format(rial / usdtRate),
    u: "USDT"
  } : {
    v: irr.format(Math.round(rial)),
    u: t("تومان", "Toman")
  };
  // The totals above are what can be used now; what an open offer, a bid or a swap holds is said beside them.
  const lockedOf = (assets: Parameters<typeof lockedSplit>[0]) => lockedSplit(assets).locked;
  const lockedAll = lockedOf(wallets.flatMap(w => w.assets));

  /** The headline figure and its unit, in whichever currency this locale reads. */
  const total = asTether ? {
    v: new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2
    }).format(totalIrr / usdtRate),
    u: "USDT"
  } : {
    v: irr.format(Math.round(totalIrr)),
    u: t("تومان", "Toman")
  };

  /**
   * Each wallet's share of the total, for the distribution bar.
   *
   * Real proportions of real balances — no minimum width, no padding to make
   * a slice visible. A network holding 0.4% draws 0.4% of the bar, because a
   * bar that lies about small holdings is worse than no bar.
   */
  const shares = totalIrr > 0 ? wallets.map(w_0 => ({
    key: `${w_0.address}-${w_0.network?.id}`,
    name: w_0.network?.name ?? "—",
    share: w_0.irrValue / totalIrr
  })).filter(s_0 => s_0.share > 0).sort((a, b) => b.share - a.share) : [];
  const chainKey = (name: unknown) => String(name ?? "").trim().toLowerCase();

  /**
   * Biggest holding first.
   *
   * The API returns wallets in creation order, which is an accident of how
   * the account was set up and tells a reader nothing. Sorted by rial value
   * the list answers "where is my money" in its first row, and the order
   * matches the distribution bar above it — which is also sorted by share, so
   * the widest slice and the top card are the same network.
   */
  const ranked = [...wallets].sort((a_0, b_0) => b_0.irrValue - a_0.irrValue);

  /** 0x1234…9abc — enough to recognise, short enough for a 288px menu. */
  const shortAddress = (a_1: unknown) => {
    const v = String(a_1 ?? "");
    return v.length > 14 ? `${v.slice(0, 6)}…${v.slice(-4)}` : v;
  };
  const copyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(address);
      window.setTimeout(() => setCopied(c_0 => c_0 === address ? null : c_0), 1600);
    } catch {
      // Clipboard access can be denied outright (permissions, insecure
      // origin). Saying so beats a button that silently does nothing.
      toast({
        title: t("کپی نشد", "Could not copy"),
        tone: "warn"
      });
    }
  };
  return <>
    <Popover align="end" className="v2-acct-menu v2-wallet-menu" label={t("کیف پول", "Wallet")} trigger={({
      open,
      toggle,
      id
    }) => <button type="button" className="v2-acct v2-wallet v2-focus" onClick={toggle} aria-expanded={open} aria-controls={id} aria-label={t("کیف پول", "Wallet")} data-open={open || undefined}>
          <Wallet aria-hidden />
          <span className="v2-wallet-sum">
            {isLoading ? <Spinner /> : <>
                {/* DOM order is reading order. The container is RTL in Persian,
                    so the figure sits first (rightmost) and the unit follows
                    it — "۲۴۷٬۶۲۰ تومان", not "تومان ۲۴۷٬۶۲۰". The figure itself
                    is bidi-isolated so its grouping separators never reorder. */}
                <bdi className="v2-wallet-fig">{total.v}</bdi>
                <small>{total.u}</small>
              </>}
          </span>
        </button>}>
      {/* THE HEADLINE PLATE. Girih ground, gold key-line, one large figure —
          the same mount the page gives anything it wants read first. */}
      <div className="v2-wallet-hero">
        <span className="v2-wallet-k">
          <Wallet aria-hidden />
          {t("موجودی کل", "Total balance")}
        </span>
        <span className="v2-wallet-v">
          {isLoading ? "…" : <bdi className="v2-wallet-fig">{total.v}</bdi>}
          <small>{total.u}</small>
        </span>
        {lockedAll > 0 && !isLoading ? <Link className="v2-wallet-held" href={href("/wallet")} role="menuitem">
            <Lock aria-hidden />
            <bdi className="v2-wallet-fig">{money(lockedAll).v}</bdi>
            <small>{money(lockedAll).u}</small>
            <span>{t("قفل‌شده", "locked")}</span>
          </Link> : null}
        {asTether ? <span className="v2-wallet-alt">
            ≈ <bdi className="v2-wallet-fig">{irr.format(Math.round(totalIrr))}</bdi> IRR
          </span> : null}

        {shares.length > 1 ? <span className="v2-wallet-split" role="img" aria-label={shares.map(s_1 => `${s_1.name} ${withPercent(pct.format(s_1.share * 100), isFa)}`).join("، ")}>
            {shares.map(s_2 => <i key={s_2.key} data-chain={chainKey(s_2.name)} style={{
            flexGrow: s_2.share
          }} />)}
          </span> : null}
      </div>

      {wallets.length === 0 && !isLoading ? <p className="v2-wallet-empty">
          {t("هنوز کیف پولی ندارید.", "You do not have a wallet yet.")}
        </p> : <>
          <div className="v2-pop-title">{t("به تفکیک شبکه", "By network")}</div>
          {ranked.map(w_1 => {
          const address_0 = String(w_1.address ?? "");
          const isCopied = copied === address_0;
          const symbol = String(w_1.nativeSymbol ?? "").toUpperCase();
          const share = totalIrr > 0 ? w_1.irrValue / totalIrr : 0;
          return <div className="v2-wallet-card" key={`${address_0}-${w_1.network?.id}`} data-chain={chainKey(w_1.network?.name)}>
                {/* The network's own mark. `sanitizeUrl` because this is a
                    backend-supplied URL going into an `src`. The initial is
                    the fallback, not the default — some networks come back
                    without an image and an empty square is worse than a
                    letter. */}
                <span className="v2-wallet-mark" aria-hidden>
                  {sanitizeUrl(w_1.network?.image) ? <Image src={sanitizeUrl(w_1.network?.image)} alt="" width={26} height={26} unoptimized /> : (w_1.network?.name ?? "?").slice(0, 1).toUpperCase()}
                </span>

                <span className="v2-wallet-id">
                  <b>{w_1.network?.name ?? "—"}</b>
                  {/* `stopPropagation`: `.v2-pop` closes on any click inside
                      it, which is right for the rows that navigate and wrong
                      here — copying should leave the menu open so the
                      confirmation is visible. */}
                  <button type="button" className="v2-wallet-addr" onClick={e => {
                e.stopPropagation();
                copyAddress(address_0);
              }} aria-label={`${t("کپی نشانی", "Copy address")} — ${address_0}`} data-copied={isCopied || undefined}>
                    <bdi>{shortAddress(address_0)}</bdi>
                    {isCopied ? <Check aria-hidden /> : <Copy aria-hidden />}
                  </button>
                </span>

                <span className="v2-wallet-val">
                  <span className="v2-wallet-amt">
                    <bdi className="v2-wallet-fig">{amount.format(w_1.nativeBalance)}</bdi>
                    <small>{symbol}</small>
                  </span>
                  {/* The rial value of this network's holding. It existed only
                      inside the total before, so the breakdown answered "how
                      much POL" without answering "how much is that". */}
                  {w_1.irrValue > 0 ? <span className="v2-wallet-irr">
                      <bdi className="v2-wallet-fig">{irr.format(Math.round(w_1.irrValue))}</bdi>
                      <small>{t("تومان", "Toman")}</small>
                    </span> : null}
                  {lockedOf(w_1.assets) > 0 ? <span className="v2-wallet-lock" title={t("قفل‌شده", "Locked")}>
                      <Lock aria-hidden />
                      <bdi className="v2-wallet-fig">{money(lockedOf(w_1.assets)).v}</bdi>
                      <small>{t("قفل", "locked")}</small>
                    </span> : null}
                </span>

                {/* This card's slice of the same bar the plate above draws,
                    on the card it belongs to. Real proportion, no minimum. */}
                {share > 0 ? <i className="v2-wallet-bar" aria-hidden style={{
              ["--v2-share" as string]: `${share * 100}%`
            }} /> : null}
              </div>;
        })}
        </>}

      <div className="v2-pop-sep" />
      {/* Buttons, not links: the menu closes on the click (as for a link) and the dialog opens in place. */}
      <button className="v2-pop-item" type="button" role="menuitem" onClick={() => setDialog("deposit")}>
        <ArrowDownToLine aria-hidden />
        <span>{t("افزایش موجودی", "Add funds")}</span>
      </button>
      <button className="v2-pop-item" type="button" role="menuitem" onClick={() => setDialog("swap")}>
        <ArrowLeftRight aria-hidden />
        <span>{t("تبدیل ارز", "Swap")}</span>
      </button>
      <Link className="v2-pop-item" href={href("/wallet")} role="menuitem">
        <Receipt aria-hidden />
        <span>{t("تاریخچه‌ی تراکنش‌ها", "Transaction history")}</span>
      </Link>
      {/* `stopPropagation`: `.v2-pop` closes on any click inside it, which is
          right for every row above — they navigate — and wrong for this one,
          which has to leave the menu open to show the new numbers. */}
      <button className="v2-pop-item" type="button" role="menuitem" onClick={e_0 => {
        e_0.stopPropagation();
        refetch();
      }}>
        <RefreshCcw aria-hidden className={isLoading ? "v2-spin" : undefined} />
        <span>{t("به‌روزرسانی موجودی", "Refresh balance")}</span>
      </button>
    </Popover>
    {/* Mounted only while open, so the header fires none of their requests until asked. */}
    {dialog === "deposit" ? <DepositDialog open onOpenChange={o => {
      if (!o) setDialog(null);
    }} lang={lang} /> : null}
    {dialog === "swap" ? <SwapDialog open onOpenChange={o_0 => {
      if (!o_0) setDialog(null);
    }} lang={lang} /> : null}
    </>;
}